import { Box, Flex, HStack } from '@televet/kibble-ui/build/chakra';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Select, SelectOptionProps } from '@televet/kibble-ui/build/components/Select';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { useGetAutomationsList } from 'pages/Automations/hooks/useGetAutomationsList';
import { ScheduledWorkflowDraft, TimeIncrement } from 'pages/Automations/types/AutomationDraft';
import React, { useCallback, useMemo } from 'react';
import { WorkflowEventTriggerType } from 'shared/types/graphql';
import { useAppSelector } from 'state/hooks';

interface ScheduledWorkflowSelectorInput {
  scheduledWorkflow: ScheduledWorkflowDraft | undefined;
  setScheduledWorkflow: React.Dispatch<React.SetStateAction<ScheduledWorkflowDraft | undefined>>;
  errors?: Record<string, boolean>;
}

const ScheduledWorkflowSelector = ({
  scheduledWorkflow,
  setScheduledWorkflow,
  errors,
}: ScheduledWorkflowSelectorInput): JSX.Element => {
  const { automationsList } = useGetAutomationsList({});
  const currentAutomationType = useAppSelector((state) => state.automations.automationDraft.automationType);
  const currentAutomationId = useAppSelector((state) => state.automations.automationDraft.automationId);

  const parentAutomations = useMemo(
    () =>
      automationsList
        .filter((automation) => automation.triggerType === WorkflowEventTriggerType.Automated)
        .filter((automation) => automation.event === currentAutomationType || automation.id === currentAutomationId),
    [automationsList, currentAutomationType, currentAutomationId],
  );

  const automationOptions: SelectOptionProps[] = useMemo(
    () =>
      parentAutomations.map(
        (automation): SelectOptionProps => ({
          value: automation.id,
          label: automation.name,
        }),
      ),
    [parentAutomations],
  );

  const timeIncrementOptions: SelectOptionProps[] = useMemo(() => {
    const amount = scheduledWorkflow?.delayAmount;
    const getPluralizedLabel = (label: string): string => `${label}${amount === 1 ? '' : 's'}`;

    return [
      {
        value: TimeIncrement.Minutes,
        label: getPluralizedLabel('Minute'),
      },
      {
        value: TimeIncrement.Hours,
        label: getPluralizedLabel('Hour'),
      },
      {
        value: TimeIncrement.Days,
        label: getPluralizedLabel('Day'),
      },
    ];
  }, [scheduledWorkflow?.delayAmount]);

  const handleWorkflowIdChange = useCallback(
    (workflowId: string | undefined) => {
      const selectedAutomation = automationsList.find((automation) => automation.id === workflowId);
      setScheduledWorkflow((current) => ({
        ...current,
        id: workflowId,
        workflowType: selectedAutomation?.event,
      }));
    },
    [automationsList, setScheduledWorkflow],
  );

  const handleWorkflowDelayAmountChange = useCallback(
    (delay: string | undefined) => {
      const delayNumber = delay ? parseInt(delay) : undefined;
      setScheduledWorkflow((current) => ({
        ...current,
        delayAmount: delayNumber,
      }));
    },
    [setScheduledWorkflow],
  );

  const handleWorkflowDelayIncrementChange = useCallback(
    (increment: TimeIncrement | undefined) => {
      setScheduledWorkflow((current) => ({
        ...current,
        delayIncrement: increment,
      }));
    },
    [setScheduledWorkflow],
  );

  return (
    <Box>
      <HStack justifyContent="flex-start" mr="44px">
        <Select
          options={automationOptions}
          value={scheduledWorkflow?.id}
          listProps={{
            onSelect: (item): void => handleWorkflowIdChange(item.value),
            isSearchable: true,
            minW: '320px',
          }}
          placeholder="Select automation..."
          defaultLeftElement={<Icon name="robot" />}
          emptyStateText="There are no automations available"
          w="231px"
          isInvalid={errors?.workflowType}
          errorText=""
        />
        <Text>in</Text>
        <TextInput
          value={scheduledWorkflow?.delayAmount}
          onChange={(e): void => handleWorkflowDelayAmountChange(e.currentTarget.value)}
          formControlStyle={{ width: 'min-content' }}
          type="number"
          w="72px"
          isInvalid={errors?.delayAmount}
          errorText=""
        />
        <Select
          options={timeIncrementOptions}
          value={scheduledWorkflow?.delayIncrement ?? TimeIncrement.Hours}
          listProps={{
            onSelect: (item): void => handleWorkflowDelayIncrementChange(item.value as TimeIncrement),
          }}
        />
      </HStack>
      {errors && (
        <Flex mt={2} direction="column" gap={1}>
          {errors.workflowType && (
            <Text as="p" fontSize="xs" color={'text.danger'}>
              Please select an automation type
            </Text>
          )}
          {errors.delayAmount && (
            <Text as="p" fontSize="xs" color={'text.danger'}>
              Please enter a valid interval
            </Text>
          )}
        </Flex>
      )}
    </Box>
  );
};

export default ScheduledWorkflowSelector;
